<template>
  <div class="survey-list-page">
    <v-window
      v-model="SurveyStore.currentPage"
      class="survey-list-page-window"
      touchless
    >
      <v-window-item value="survey-list">
        <div class="survey-list">
          <PageHeader header="Surveys" icon="mdi-clipboard-text-outline" />
          <PageContent>
            <v-data-table
              class="survey-list-table"
              :headers="tableHeaders"
              :items="SurveyStore.surveyList"
              :mobile-breakpoint="768"
              :search="SurveyStore.surveySearchTerm"
              :server-items-length="SurveyStore.totalSurveys"
              :loading="SurveyStore.isSurveyListLoading"
              loading-text="Loading surveys..."
              @click:row="openSurvey"
              @update:options="updateOptions"
            >
              <template v-slot:top>
                <v-text-field
                  v-model="SurveyStore.surveySearchTerm"
                  label="Search"
                  class="mx-4 survey-list-search"
                  append-icon="mdi-magnify"
                  clearable
                  aria-label="Search surveys"
                  @input="debouncedSearch"
                ></v-text-field>
              </template>
              <template #[`item.name`]="{ item }">
                <div class="survey-row-name">
                  <span class="survey-row-title">{{ item.name }}</span>
                  <StatusChip
                    :label="Number(item.percent) >= 100 ? 'Complete' : 'Active'"
                    :color="Number(item.percent) >= 100 ? '#2E7D32' : '#1565C0'"
                    variant="soft"
                  />
                </div>
              </template>
              <template #[`item.percent`]="{ item }">
                <ProgressRing
                  :percent="Number(item.percent)"
                  :size="44"
                  :stroke-width="5"
                  :color="ringColor(Number(item.percent))"
                  :show-label="false"
                  class="survey-row-ring"
                />
                <span class="survey-row-pct">{{ Math.round(Number(item.percent)) }}%</span>
              </template>
              <template v-slot:no-data>
                <div class="survey-list-empty">
                  <v-icon size="56" color="#CFD8DC">mdi-clipboard-text-outline</v-icon>
                  <h3>No surveys yet</h3>
                  <p>Surveys you create or are assigned to will appear here.</p>
                </div>
              </template>
            </v-data-table>
          </PageContent>
        </div>
      </v-window-item>

      <v-window-item eager value="survey-detail">
        <div class="survey-detail">
          <PageHeader
            :header="header"
            icon="mdi-arrow-left"
            :iconClick="SurveyStore.backButton"
          />
          <PageContent>
            <div v-if="SurveyStore.isSurveyLoading" class="survey-detail-skeleton">
              <v-skeleton-loader type="image" height="140" class="mb-4" />
              <v-skeleton-loader type="list-item-two-line" />
              <v-skeleton-loader type="list-item-two-line" />
              <v-skeleton-loader type="table-heading, table-row@4" class="mt-4" />
            </div>
            <SurveyPage v-show="!SurveyStore.isSurveyLoading" :backButtonClick="SurveyStore.backButton" />
          </PageContent>
        </div>
      </v-window-item>

      <v-window-item v-if="LayoutStore.isMobile()" eager value="map-view">
        <div class="survey-detail">
          <PageHeader
            :header="header"
            icon="mdi-arrow-left"
            :iconClick="SurveyStore.backButton"
          />
          <PageContent>
            <MapContainer />
          </PageContent>
        </div>
      </v-window-item>

      <v-window-item eager value="place-detail">
        <PlaceDetailPage />
      </v-window-item>
    </v-window>
  </div>
</template>

<script>
import _ from "lodash";

import MapContainer from "@/components/MapContainer.vue";
import PageContent from "@/components/pages/PageContent.vue";
import PageHeader from "@/components/pages/PageHeader.vue";
import PlaceDetailPage from "@/components/pages/place/PlaceDetailPage.vue";
import SurveyPage from "@/components/pages/survey/SurveyPage.vue";
import ProgressRing from "@/components/viz/ProgressRing.vue";
import StatusChip from "@/components/viz/StatusChip.vue";

import SurveyService from "@/services/SurveyService";

import { LayoutStore } from "@/stores/LayoutStore.js";
import { NavigationStore } from "@/stores/NavigationStore.js";
import { SurveyStore, SurveyPages } from "@/stores/SurveyStore.js";

export const SurveyListPageLayer = "SurveyListPage";
export const SurveyListPageMarkerLayer = `${SurveyListPageLayer}-Markers`;

const HEADERS = [
  {
    cellClass: "survey-name-column",
    text: "Name",
    align: "start",
    value: "name",
  },
  { text: "% Complete", value: "percent" },
];

export default {
  name: "SurveyListPage",
  components: {
    MapContainer,
    PageContent,
    PageHeader,
    PlaceDetailPage,
    ProgressRing,
    StatusChip,
    SurveyPage,
  },
  data() {
    return {
      header: "",
      tableHeaders: HEADERS,

      LayoutStore,
      NavigationStore,
      SurveyStore,
    };
  },
  methods: {
    ringColor: function (percent) {
      if (percent >= 80) return "#2E7D32";
      if (percent >= 40) return "#1565C0";
      return "#F57C00";
    },
    debouncedSearch: _.debounce(function () {
      SurveyStore.fetchSurveys();
    }, 300),
    openSurvey: function (survey) {
      this.header = survey.name || "";
      SurveyStore.isSurveyLoading = true;
      SurveyStore.navigateToPage(SurveyPages.DETAIL);

      // get survey details
      SurveyService.getSurvey(survey.id).then((response) => {
        SurveyStore.survey = response;
        SurveyStore.places = _.get(response, "places");
        SurveyStore.isSurveyLoading = false;
        // Backfill the header for deep links where only the id was known.
        if (!this.header) {
          this.header = _.get(response, "name", "");
        }
      });
    },
    updateOptions: function (options) {
      SurveyStore.surveyListOptions = options;
      SurveyStore.fetchSurveys();
    },
  },
  mounted() {
    // register callback
    NavigationStore.registerChangeListener("SurveyListPage", () =>
      SurveyStore.clearState()
    );

    // If we were navigated here to open a specific survey (e.g. from the
    // dashboard), open it now that the detail view is mounted.
    if (SurveyStore.pendingSurvey) {
      const survey = SurveyStore.pendingSurvey;
      SurveyStore.pendingSurvey = undefined;
      this.$nextTick(() => this.openSurvey(survey));
    }
  },
  props: {},
  watch: {
    "NavigationStore.mobileMapView"(newValue) {
      if (newValue === true) {
        SurveyStore.navigateToPage(SurveyPages.MAP);
      }
    },
  },
};
</script>

<style>
.survey-list-page {
  height: 100%;
  width: 100%;
}

.survey-list-page-window,
.survey-list-page-window > div:first-child {
  height: inherit;
  transition: 0.5s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.survey-list-page tbody tr {
  cursor: pointer;
}

.survey-name-column {
  max-width: 238px;
}

.btn-container {
  display: inline-block;
  position: relative;
  height: 1em;
}

.btn-container button {
  position: absolute;
}

.survey-list {
  width: 100%;
  height: 100%;
}

.survey-detail {
  position: absolute;
  width: 100%;
  height: 100%;
}

.survey-list-search {
  padding-top: 18px;
}

.survey-list-table .v-data-footer {
  justify-content: right;
  padding: 10px;
}

.survey-row-name {
  display: flex;
  align-items: center;
  gap: 10px;
}

.survey-row-title {
  font-weight: 500;
  color: #263238;
}

.survey-row-ring {
  vertical-align: middle;
  margin-right: 8px;
}

.survey-row-pct {
  font-size: 0.85rem;
  font-weight: 600;
  color: #607D8B;
  vertical-align: middle;
}

.survey-list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  color: #90A4AE;
}

.survey-list-empty h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #546E7A;
  margin: 16px 0 4px;
}

.survey-list-empty p {
  font-size: 0.875rem;
  margin: 0;
  max-width: 280px;
  text-align: center;
}

.survey-detail-skeleton {
  padding: 16px;
}

.survey-list-table .v-data-footer__select {
  display: none;
}
</style>
